import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Radio, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Big in-your-face "We're Live" hero card for the landing page.
 * Renders nothing when no live event is active.
 */
export const LiveEventHeroCard = () => {
  const [live, setLive] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/live-stream`);
        if (cancelled) return;
        if (res.data?.is_live && res.data?.embed_code) {
          setLive(res.data);
        } else {
          setLive(null);
        }
      } catch { /* ignore */ }
    };
    poll();
    const id = setInterval(poll, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (!live) return null;

  return (
    <Link
      to="/events"
      className="block group mt-10 mx-auto max-w-3xl"
      data-testid="live-event-hero-card"
    >
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-red-600 via-red-700 to-red-900 shadow-2xl ring-4 ring-red-500/30 group-hover:ring-red-500/60 group-hover:scale-[1.02] transition-all animate-pulse-slow">
        {/* Animated background sweep */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />

        {/* Pulsing border glow */}
        <div className="absolute inset-0 ring-2 ring-white/30 rounded-3xl animate-pulse pointer-events-none" />

        <div className="relative px-6 py-7 sm:px-10 sm:py-8 flex items-center gap-5 sm:gap-7 text-white">
          {/* Big pulsing live dot */}
          <div className="relative flex-shrink-0">
            <span className="relative flex h-5 w-5 sm:h-6 sm:w-6">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
              <span className="relative inline-flex rounded-full h-5 w-5 sm:h-6 sm:w-6 bg-white"></span>
            </span>
          </div>

          <div className="flex-1 min-w-0 text-left">
            <div className="flex items-center gap-2 mb-1">
              <Radio className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="font-display font-black uppercase tracking-widest text-xs sm:text-sm">
                Live Right Now
              </span>
            </div>
            <p className="font-display font-black text-2xl sm:text-3xl md:text-4xl uppercase tracking-tight leading-tight truncate">
              {live.title}
            </p>
            <p className="text-sm sm:text-base text-white/90 mt-1 font-medium">
              Don't miss it — watch live
            </p>
          </div>

          <div className="hidden sm:flex flex-shrink-0 items-center gap-2 bg-white text-red-600 px-6 py-3 rounded-full font-display font-bold uppercase tracking-wider shadow-lg group-hover:scale-110 transition-transform">
            Watch Now
            <ArrowRight className="w-5 h-5" />
          </div>

          {/* Mobile inline arrow */}
          <ArrowRight className="sm:hidden flex-shrink-0 w-6 h-6 text-white" />
        </div>
      </div>
    </Link>
  );
};

export default LiveEventHeroCard;
